import type { FeatureToggles } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type { ShortCircuitScenarioElementCalculation } from '../../..';
import type { ForFaultsEvaluation } from '../../../evaluation/shortCircuit/ForFaultsEvaluation';
import { evaluateProtectingReleaseTime } from '../../../evaluation/shortCircuit/singleElement/evaluateGridElement';
import { FaultEvaluation } from '../../../evaluation/shortCircuit/singleElement/faultEvaluation';
import {
  findGridElementLabel,
  findGridElementTitle
} from '../findGridElementLabel';
import type { GridElementShortCircuitDetailsWithResults } from './ShortCircuitDetails';
import { Features } from '../../../createGridElementCalculationSetExecutionDetails';

export class DetailsWithResultsFactory {
  private constructor(
    private readonly features: Features,
    private readonly shortCircuitScenarios: ShortCircuitScenarioElementCalculation[],
    private readonly evaluationEngine: ForFaultsEvaluation,
    private readonly featureToggles?: FeatureToggles
  ) {}

  static create = (
    features: Features,
    shortCircuitScenarios: ShortCircuitScenarioElementCalculation[],
    evaluationEngine: ForFaultsEvaluation,
    featureToggles?: FeatureToggles
  ): DetailsWithResultsFactory => {
    return new DetailsWithResultsFactory(
      features,
      shortCircuitScenarios,
      evaluationEngine,
      featureToggles
    );
  };

  for(
    objectType: GridElementShortCircuitDetailsWithResults['objectType']
  ): GridElementShortCircuitDetailsWithResults[] {
    return this.createDetailsByGridElementType(
      objectType,
      this.features,
      this.shortCircuitScenarios,
      this.evaluationEngine,
      this.featureToggles
    );
  }

  private createDetailsByGridElementType = (
    objectType: GridElementShortCircuitDetailsWithResults['objectType'],
    features: Features,
    shortCircuitScenarios: ShortCircuitScenarioElementCalculation[],
    evaluationEngine: ForFaultsEvaluation,
    featureToggles?: FeatureToggles
  ): GridElementShortCircuitDetailsWithResults[] => {
    return shortCircuitScenarios
      .filter(gridElement => gridElement.objectType === objectType)
      .map(gridElement =>
        this.createGridElementWithResultsDetails(
          features,
          gridElement,
          evaluationEngine,
          objectType,
          featureToggles
        )
      );
  };

  private createGridElementWithResultsDetails = (
    features: Features,
    gridElement: ShortCircuitScenarioElementCalculation,
    evaluationEngine: ForFaultsEvaluation,
    objectType: GridElementShortCircuitDetailsWithResults['objectType'],
    featureToggles?: FeatureToggles
  ): GridElementShortCircuitDetailsWithResults => {
    const faultEvaluation = FaultEvaluation.create(
      gridElement,
      evaluationEngine
    );
    return {
      label: findGridElementLabel(features, gridElement.id),
      title: findGridElementTitle(features, gridElement.id),
      id: gridElement.id,
      objectType,
      results: {
        protectingReleaseTime: evaluateProtectingReleaseTime(
          gridElement.result.releaseTime,
          !featureToggles?.isProtectiveDevicesEnabled
        ),
        phase1Min: faultEvaluation.onePhaseToGroundMin(),
        phase1Max: faultEvaluation.onePhaseToGroundMax(),
        phase2Min: faultEvaluation.twoPhasesMin(),
        phase3Max: faultEvaluation.threePhasesMax()
      }
    };
  };
}
